/**
 * Register Schemas Utility
 *
 * Provides functions to register custom event schemas for components.
 */

import { eventSchemas } from './event-schemas';
import type { SchemaDefinition } from './event-schemas';

/**
 * Register custom event schemas for a component
 * 
 * @param elementType - The ID of the component registering schemas
 * @param schemas - Object containing event schemas to register
 * @returns Boolean indicating if registration was successful
 */
export function registerElementEventSchemas(
  elementType: string,
  schemas: Record<string, SchemaDefinition>
): boolean {
  try {
    // Add prefix to event names to avoid collisions
    const prefixedSchemas = Object.entries(schemas).reduce(
      (acc, [eventName, schema]) => {
        // Use format: elementType:eventName
        const prefixedName = `${elementType}:${eventName}`;
        acc[prefixedName] = schema;
        return acc;
      },
      {} as Record<string, SchemaDefinition>
    );

    // Add schemas to the global eventSchemas object
    Object.assign(eventSchemas, prefixedSchemas);
    
    // Log registration for debugging
    console.debug(`Registered ${Object.keys(prefixedSchemas).length} event schemas for ${elementType}`);
    
    return true;
  } catch (error) {
    console.error(`Failed to register event schemas for ${elementType}:`, error);
    return false;
  }
}

/**
 * Get all registered event schemas for a component
 * 
 * @param elementType - The ID of the component
 * @returns Object containing registered event schemas for the component
 */
export function getElementEventSchemas(
  elementType: string
): Record<string, SchemaDefinition> {
  const componentSchemas: Record<string, SchemaDefinition> = {};
  
  // Find all schemas with the component prefix
  Object.entries(eventSchemas).forEach(([eventName, schema]) => {
    if (eventName.startsWith(`${elementType}:`)) {
      // Remove the prefix for cleaner access
      const unprefixedName = eventName.replace(`${elementType}:`, '');
      componentSchemas[unprefixedName] = schema;
    }
  });
  
  return componentSchemas;
}

export default {
  registerElementEventSchemas,
  getElementEventSchemas
};
